import React, { useEffect, useRef, useState } from 'react';
import { X, UserPlus, AlertCircle, Search, Shield } from 'lucide-react';
import { getCookie } from '../utils/utils';

interface InviteResult {
  created: { email: string }[];
  updated: { email: string }[];
  skipped: { email: string; reason: string }[];
  pending: { email: string; reason: string }[];
  errors: { email: string; error: string }[];
}

interface CatalyxUser {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
}

interface InviteUsersModalProps {
  orgId: number;
  canAssignCreatorViewer: boolean;
  canAssignCatalyxUser: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const displayName = (user: CatalyxUser) => {
  const full = `${user.first_name || ''} ${user.last_name || ''}`.trim();
  return full || user.email;
};

const InviteUsersModal: React.FC<InviteUsersModalProps> = ({
  orgId,
  canAssignCreatorViewer,
  canAssignCatalyxUser,
  onClose,
  onSuccess,
}) => {
  const [emailsText, setEmailsText] = useState('');
  const [roleKey, setRoleKey] = useState('creator');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<InviteResult | null>(null);

  const [catalyxQuery, setCatalyxQuery] = useState('');
  const [catalyxResults, setCatalyxResults] = useState<CatalyxUser[]>([]);
  const [selectedCatalyxUsers, setSelectedCatalyxUsers] = useState<CatalyxUser[]>([]);
  const [isCatalyxSearching, setIsCatalyxSearching] = useState(false);
  const [showCatalyxDropdown, setShowCatalyxDropdown] = useState(false);
  const catalyxDropdownRef = useRef<HTMLDivElement>(null);

  const roleOptions = [
    { key: 'creator', label: 'Creator' },
    { key: 'viewer', label: 'Viewer' },
  ];

  useEffect(() => {
    if (!canAssignCatalyxUser || catalyxQuery.trim().length < 2) {
      setCatalyxResults([]);
      setShowCatalyxDropdown(false);
      return;
    }

    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setIsCatalyxSearching(true);
      try {
        const response = await fetch(
          `/api/users/search-catalyx-support/?q=${encodeURIComponent(catalyxQuery.trim())}&org_id=${orgId}`,
          { credentials: 'include' }
        );
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (cancelled) return;
        const existingIds = new Set(selectedCatalyxUsers.map((u) => u.id));
        const users = (Array.isArray(data) ? data : []).filter(
          (u: CatalyxUser) => !existingIds.has(u.id)
        );
        setCatalyxResults(users);
        setShowCatalyxDropdown(users.length > 0);
      } catch {
        if (!cancelled) {
          setCatalyxResults([]);
          setShowCatalyxDropdown(false);
        }
      } finally {
        if (!cancelled) setIsCatalyxSearching(false);
      }
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [catalyxQuery, canAssignCatalyxUser, orgId, selectedCatalyxUsers]);

  useEffect(() => {
    const onDocClick = (event: MouseEvent) => {
      if (
        catalyxDropdownRef.current &&
        !catalyxDropdownRef.current.contains(event.target as Node)
      ) {
        setShowCatalyxDropdown(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResults(null);

    if (!canAssignCreatorViewer && selectedCatalyxUsers.length === 0) {
      setError('Select at least one Catalyx user to add.');
      return;
    }

    setIsSubmitting(true);
    try {
      const csrfToken = getCookie('csrftoken');

      if (canAssignCreatorViewer && emailsText.trim()) {
        const emails = emailsText
          .split(/[\n,;]+/)
          .map((addr) => addr.trim().toLowerCase())
          .filter((addr) => addr.length > 0);

        if (emails.length === 0 && selectedCatalyxUsers.length === 0) {
          setError('Please enter at least one email address.');
          setIsSubmitting(false);
          return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const invalid = emails.filter((addr) => !emailRegex.test(addr));
        if (invalid.length > 0) {
          setError(`Invalid email format: ${invalid.join(', ')}`);
          setIsSubmitting(false);
          return;
        }

        if (emails.length > 0) {
          const response = await fetch('/api/org-members/invite/', {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRFToken': csrfToken || '',
            },
            body: JSON.stringify({ org_id: orgId, role_key: roleKey, emails }),
          });

          if (!response.ok) {
            const data = await response.json();
            setError(data?.error || data?.detail || 'Failed to invite users.');
            setIsSubmitting(false);
            return;
          }

          const data: InviteResult = await response.json();
          setResults(data);
          if (data.errors?.length && selectedCatalyxUsers.length === 0) {
            setIsSubmitting(false);
            return;
          }
        }
      }

      for (const user of selectedCatalyxUsers) {
        const response = await fetch('/api/org-memberships/', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRFToken': csrfToken || '',
          },
          body: JSON.stringify({ org: orgId, user: user.id }),
        });
        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          setError(
            data?.detail ||
              data?.error ||
              `Failed to add ${displayName(user)} to the organization.`
          );
          setIsSubmitting(false);
          return;
        }
      }

      onSuccess();
      onClose();
    } catch {
      setError('An error occurred while inviting users.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const canSubmit =
    (canAssignCreatorViewer && emailsText.trim().length > 0) ||
    selectedCatalyxUsers.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <UserPlus className="w-5 h-5" />
            Invite Users
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          {canAssignCreatorViewer && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Addresses
                </label>
                <textarea
                  value={emailsText}
                  onChange={(ev) => setEmailsText(ev.target.value)}
                  placeholder="Enter email addresses (one per line, or separated by commas)"
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={isSubmitting}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={roleKey}
                  onChange={(ev) => setRoleKey(ev.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  disabled={isSubmitting}
                >
                  {roleOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  New users get this role. Existing users keep their current role when added to the
                  organization.
                </p>
              </div>
            </>
          )}

          {canAssignCatalyxUser && (
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                <Shield className="w-4 h-4 text-[#807CB1]" />
                Add Catalyx users
              </label>
              <div className="relative" ref={catalyxDropdownRef}>
                <div className="relative">
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={catalyxQuery}
                    onChange={(e) => setCatalyxQuery(e.target.value)}
                    className="w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-xl bg-white placeholder-gray-400
                      focus:outline-none focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500
                      transition-all duration-200 hover:border-gray-300"
                    placeholder="Search Catalyx users..."
                    disabled={isSubmitting}
                  />
                  {isCatalyxSearching && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-200 border-t-indigo-600" />
                    </div>
                  )}
                </div>
                {showCatalyxDropdown && catalyxResults.length > 0 && (
                  <div className="absolute z-20 w-full mt-1.5 bg-white border border-gray-200 rounded-xl shadow-xl max-h-48 overflow-y-auto">
                    {catalyxResults.map((user) => (
                      <button
                        key={user.id}
                        type="button"
                        onClick={() => {
                          setSelectedCatalyxUsers((prev) => [...prev, user]);
                          setCatalyxQuery('');
                          setCatalyxResults([]);
                          setShowCatalyxDropdown(false);
                        }}
                        className="w-full px-4 py-2.5 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-50 last:border-b-0"
                      >
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#807CB1] to-[#5D5A8C] flex items-center justify-center flex-shrink-0">
                          <span className="text-white text-sm font-medium">
                            {(user.first_name?.[0] || user.email[0]).toUpperCase()}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {displayName(user)}
                          </p>
                          <p className="text-xs text-gray-500 truncate">{user.email}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {selectedCatalyxUsers.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {selectedCatalyxUsers.map((user) => (
                    <div
                      key={user.id}
                      className="flex items-center gap-2 bg-[#807CB1]/10 border border-[#807CB1]/25 rounded-full pl-1 pr-2 py-1"
                    >
                      <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#807CB1] to-[#5D5A8C] flex items-center justify-center">
                        <span className="text-white text-xs font-medium">
                          {(user.first_name?.[0] || user.email[0]).toUpperCase()}
                        </span>
                      </div>
                      <span className="text-sm text-[#5D5A8C] truncate max-w-[140px]">
                        {displayName(user)}
                      </span>
                      <button
                        type="button"
                        onClick={() =>
                          setSelectedCatalyxUsers((prev) => prev.filter((u) => u.id !== user.id))
                        }
                        className="text-[#807CB1]/50 hover:text-[#807CB1]"
                        disabled={isSubmitting}
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              <p className="mt-1 text-xs text-gray-500">
                Catalyx users keep their Catalyx role; this only adds them to the organization.
              </p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {results && (
            <div className="space-y-2">
              {results.created.length > 0 && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  <p className="text-sm text-green-800">
                    Added: {results.created.map((r) => r.email).join(', ')}
                  </p>
                </div>
              )}
              {results.updated.length > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-800">
                    Updated: {results.updated.map((r) => r.email).join(', ')}
                  </p>
                </div>
              )}
              {(results.pending || []).length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-900">
                    Waiting for confirmation: {(results.pending || []).map((r) => `${r.email} (${r.reason})`).join(', ')}
                  </p>
                </div>
              )}
              {results.skipped.length > 0 && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-sm text-gray-700">
                    Skipped: {results.skipped.map((r) => `${r.email} (${r.reason})`).join(', ')}
                  </p>
                </div>
              )}
              {results.errors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-800">
                    Errors: {results.errors.map((r) => `${r.email}: ${r.error}`).join(', ')}
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !canSubmit}
              className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Inviting...' : 'Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InviteUsersModal;
